// DS5.10
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks/useEnabledFlagList';
import ListMenu, { ListMenuItem } from '../LegacyListMenu';
import classnames from 'classnames';
import SubmitButton from './SubmitButton';
import SubmitMenuItem from './SubmitMenuItem';
import { desktopSubmitMenuItems } from '../../constants';
import TierCreatorReduxModule from '../../../Tier/redux/creator/ModuleLoadable';
import s from './SubmitMenu.scss';

export interface Props {
  isOpen: boolean;
  className?: string;
  menuClassName?: string;
  toggleSubmitMenu: () => void;
}

const SubmitMenu: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  menuClassName,
  isOpen,
  toggleSubmitMenu,
}) => {
  const { t } = useTranslation();
  const flags = useEnabledFlagList();

  const submitMenuItems = useMemo(() => {
    return desktopSubmitMenuItems
      .filter(item => item && (!item.feature || flags.includes(item.feature)))
      .map(
        ({
          type,
          label,
          target,
          verificationAction = 'submit',
          link,
          modalType,
          modalOptions,
          imageFileName,
          imageMarginBottom,
          imageMarginLeft,
          showNewByTitle,
          showDaProtect,
          openInStudio,
        }): ListMenuItem => {
          let linkUrl: string;
          if (link) {
            linkUrl = Url.buildFromPath(link()); // only runtime can generate correct url on server
          } else {
            linkUrl = URLS.ROOT;
          }
          return {
            key: label,
            renderElement: () => (
              <SubmitMenuItem
                role="menuitem"
                className={s['submit-menu-item']}
                hoverComponentClassName={s['hover-component']}
                type={type}
                modalType={modalType}
                modalOptions={modalOptions}
                imageFileName={imageFileName}
                imageMarginBottom={imageMarginBottom}
                imageMarginLeft={imageMarginLeft}
                label={label}
                link={linkUrl}
                target={target}
                verificationAction={verificationAction}
                showDaProtect={showDaProtect}
                showNewByTitle={showNewByTitle}
                openInStudio={openInStudio}
              />
            ),
          };
        }
      );
  }, [flags]);

  return (
    <>
      {isOpen && <TierCreatorReduxModule />}
      <div className={classnames(s['submit-dropdown'], className)}>
        <div className={s['submit-button-link-wrapper']}>
          <SubmitButton
            className={classnames(
              s['submit-button'],
              isOpen && s['submit-button-open']
            )}
            onClick={toggleSubmitMenu}
            id="site-header-submit-button"
          >
            {t('siteHeader.submit')}
          </SubmitButton>
        </div>
        <div className={s['submit-menu-wrapper']}>
          <ListMenu
            className={classnames(
              menuClassName,
              'ds-surface-tertiary',
              s['submit-menu']
            )}
            items={submitMenuItems}
            containerRole="menu"
            aria-labelledby="site-header-submit-button"
          />
        </div>
      </div>
    </>
  );
};
SubmitMenu.displayName = 'SubmitMenu';

export default SubmitMenu;
